// src/pages/manager/ManagerDashboard.jsx

import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    FaUsers,
    FaUserNinja,
    FaWallet,
    FaArrowRight,
    FaCheckCircle,
    FaExclamationCircle,
    FaPlus,
    FaTrophy,
    FaSyncAlt,
    FaLayerGroup,
    FaClock,
    FaTimesCircle,
    FaInfoCircle,
    FaReceipt
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { AppContext } from '../../context/AppContext';

// ======================================================================
// HELPER
// ======================================================================
const normalizeText = (value) => String(value || '').toLowerCase().trim();

const safeNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const firstValue = (...values) => {
    for (const value of values) {
        if (value !== undefined && value !== null && value !== '') return value;
    }

    return 0;
};

const formatRupiah = (value) => {
    return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0
    }).format(safeNumber(value));
};

const formatDate = (value) => {
    if (!value) return '-';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });
};

const getEntryMembers = (athlete) => {
    if (!athlete) return [];

    if (Array.isArray(athlete.entry_members)) {
        return athlete.entry_members
            .map(member => String(member || '').trim())
            .filter(Boolean);
    }

    if (typeof athlete.entry_members === 'string' && athlete.entry_members.trim() !== '') {
        try {
            const parsed = JSON.parse(athlete.entry_members);

            if (Array.isArray(parsed)) {
                return parsed
                    .map(member => String(member || '').trim())
                    .filter(Boolean);
            }
        } catch (error) {
            return athlete.entry_members
                .split(/\s*(?:\/|\||,|\+|&|\bdan\b|\n)\s*/i)
                .map(member => String(member || '').trim())
                .filter(Boolean);
        }
    }

    return [];
};

const getDisplayName = (athlete) => {
    const members = getEntryMembers(athlete);

    if (members.length > 1) {
        return members.join(' / ');
    }

    return athlete?.name || '-';
};

const getPoomsaeCategoryLabel = (category) => {
    const value = normalizeText(category);

    if (value.includes('freestyle') || value.includes('free style')) return 'Freestyle';
    if (value.includes('pair') || value.includes('pasangan') || value.includes('ganda')) return 'Pair';
    if (value.includes('team') || value.includes('regu') || value.includes('kelompok')) return 'Team';
    if (value.includes('individual') || value.includes('individu')) return 'Individual';

    return category || 'Individual';
};

const getPoomsaeCategoryKey = (category) => {
    const value = normalizeText(category);

    if (value.includes('freestyle') || value.includes('free style')) return 'freestyle';
    if (value.includes('pair') || value.includes('pasangan') || value.includes('ganda')) return 'pair';
    if (value.includes('team') || value.includes('regu') || value.includes('kelompok')) return 'team';

    return 'individual';
};

const getClassLabel = (athlete) => {
    if (!athlete) return '-';

    if (athlete.competition_type === 'Kyorugi') {
        const beltText = athlete.belt ? ` • Sabuk ${athlete.belt}` : '';
        return `${athlete.weight_class || '-'}${beltText}`;
    }

    return getPoomsaeCategoryLabel(athlete.poomsae_category);
};

const getBillingLabel = (key) => {
    const labels = {
        team_registration: 'Biaya Pendaftaran Kontingen',
        kyorugi: 'Kyorugi',
        poomsae_individual: 'Poomsae Individual',
        poomsae_freestyle: 'Poomsae Freestyle',
        poomsae_pair: 'Poomsae Pair',
        poomsae_team: 'Poomsae Team',
        other: 'Lainnya',
        unknown: 'Lainnya'
    };

    return labels[key] || key || 'Lainnya';
};

const normalizeBillingBreakdown = (rawBreakdown = []) => {
    if (!Array.isArray(rawBreakdown)) return [];

    return rawBreakdown
        .map((item) => {
            const key = item.key || item.type || item.code || 'unknown';
            const count = safeNumber(firstValue(item.qty, item.count, item.quantity));
            const total = safeNumber(firstValue(item.amount, item.total, item.subtotal));
            const price = safeNumber(firstValue(item.unit_price, item.price, count > 0 ? total / count : 0));

            return {
                key,
                label: item.label || getBillingLabel(key),
                count,
                price,
                total
            };
        })
        .filter(item => item.count > 0 || item.total > 0);
};

const getStatusBadge = (status, outstandingAmount) => {
    if (outstandingAmount <= 0) {
        return {
            label: 'Lunas',
            className: 'bg-success-subtle text-success border border-success',
            icon: <FaCheckCircle />
        };
    }

    if (status === 'Pending') {
        return {
            label: 'Menunggu Verifikasi',
            className: 'bg-warning-subtle text-warning border border-warning',
            icon: <FaClock />
        };
    }

    if (status === 'Rejected') {
        return {
            label: 'Ditolak',
            className: 'bg-danger-subtle text-danger border border-danger',
            icon: <FaTimesCircle />
        };
    }

    return {
        label: 'Belum Dibayar',
        className: 'bg-danger-subtle text-danger border border-danger',
        icon: <FaExclamationCircle />
    };
};

const ManagerDashboard = () => {
    const { user } = useContext(AppContext);

    const [dashboardData, setDashboardData] = useState(null);
    const [athletes, setAthletes] = useState([]);
    const [paymentSummary, setPaymentSummary] = useState(null);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    // ======================================================================
    // FETCH DASHBOARD DATA
    // ======================================================================
    const fetchDashboard = async (silent = false) => {
        if (silent) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }

        try {
            const [dashboardResult, athletesResult, paymentResult] = await Promise.allSettled([
                api.get('/dashboard/manager'),
                api.get('/athletes/my-athletes'),
                api.get('/payments/my-payment')
            ]);

            if (dashboardResult.status === 'fulfilled') {
                setDashboardData(dashboardResult.value.data || {});
            }

            if (athletesResult.status === 'fulfilled') {
                const athleteData =
                    athletesResult.value.data.result ||
                    athletesResult.value.data.data ||
                    athletesResult.value.data ||
                    [];

                setAthletes(Array.isArray(athleteData) ? athleteData : []);
            }

            if (paymentResult.status === 'fulfilled') {
                setPaymentSummary(paymentResult.value.data || {});
            }

            if (
                dashboardResult.status === 'rejected' &&
                athletesResult.status === 'rejected' &&
                paymentResult.status === 'rejected'
            ) {
                toast.error('Gagal memuat dashboard manager.');
            }
        } catch (error) {
            console.error('Gagal memuat dashboard:', error);
            toast.error('Gagal memuat dashboard manager.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ======================================================================
    // DERIVED DATA
    // ======================================================================
    const computedStats = useMemo(() => {
        const oldStats = dashboardData?.stats || {};

        const dashboardBilling =
            dashboardData?.billing_summary ||
            dashboardData?.billingSummary ||
            oldStats?.billing_summary ||
            oldStats?.billingSummary ||
            {};

        const paymentBilling =
            paymentSummary?.billing_summary ||
            paymentSummary?.billingSummary ||
            {};

        const latestPayment =
            paymentSummary?.latest_payment ||
            paymentSummary?.latestPayment ||
            dashboardData?.latestPayment ||
            null;

        const totalEntries = athletes.length || safeNumber(oldStats.totalAthletes);

        const kyorugiEntries = athletes.length
            ? athletes.filter(athlete => athlete.competition_type === 'Kyorugi').length
            : safeNumber(oldStats.kyorugiAthletes);

        const poomsaeEntries = athletes.length
            ? athletes.filter(athlete => athlete.competition_type === 'Poomsae').length
            : safeNumber(oldStats.poomsaeAthletes);

        const prestasiEntries = athletes.length
            ? athletes.filter(athlete => normalizeText(athlete.type) === 'prestasi').length
            : safeNumber(oldStats.prestasiAthletes);

        const festivalEntries = athletes.length
            ? athletes.filter(athlete => normalizeText(athlete.type) === 'festival').length
            : safeNumber(oldStats.festivalAthletes);

        const kyorugiPrestasi = athletes.length
            ? athletes.filter(athlete =>
                athlete.competition_type === 'Kyorugi' &&
                normalizeText(athlete.type) === 'prestasi'
            ).length
            : safeNumber(oldStats.kyorugiPrestasi);

        const kyorugiFestival = athletes.length
            ? athletes.filter(athlete =>
                athlete.competition_type === 'Kyorugi' &&
                normalizeText(athlete.type) === 'festival'
            ).length
            : safeNumber(oldStats.kyorugiFestival);

        const poomsaeIndividual = athletes.length
            ? athletes.filter(athlete =>
                athlete.competition_type === 'Poomsae' &&
                getPoomsaeCategoryKey(athlete.poomsae_category) === 'individual'
            ).length
            : safeNumber(oldStats.poomsaeIndividual);

        const poomsaeFreestyle = athletes.length
            ? athletes.filter(athlete =>
                athlete.competition_type === 'Poomsae' &&
                getPoomsaeCategoryKey(athlete.poomsae_category) === 'freestyle'
            ).length
            : safeNumber(oldStats.poomsaeFreestyle);

        const poomsaePair = athletes.length
            ? athletes.filter(athlete =>
                athlete.competition_type === 'Poomsae' &&
                getPoomsaeCategoryKey(athlete.poomsae_category) === 'pair'
            ).length
            : safeNumber(oldStats.poomsaePair);

        const poomsaeTeam = athletes.length
            ? athletes.filter(athlete =>
                athlete.competition_type === 'Poomsae' &&
                getPoomsaeCategoryKey(athlete.poomsae_category) === 'team'
            ).length
            : safeNumber(oldStats.poomsaeTeam);

        const totalBill = safeNumber(firstValue(
            paymentBilling.total_registered_amount,
            paymentBilling.total_bill_amount,
            paymentBilling.total_bill,
            paymentBilling.totalRegisteredAmount,
            paymentBilling.totalBillAmount,
            paymentBilling.totalBill,
            paymentSummary?.total_registered_amount,
            paymentSummary?.total_bill_amount,
            paymentSummary?.totalBill,
            dashboardBilling.total_registered_amount,
            dashboardBilling.total_bill_amount,
            dashboardBilling.total_bill,
            dashboardBilling.totalRegisteredAmount,
            dashboardBilling.totalBillAmount,
            dashboardBilling.totalBill,
            oldStats.totalRegisteredAmount,
            oldStats.totalBill
        ));

        const totalVerified = safeNumber(firstValue(
            paymentBilling.total_verified_amount,
            paymentBilling.totalVerifiedAmount,
            paymentSummary?.total_verified_amount,
            paymentSummary?.totalVerifiedAmount,
            dashboardBilling.total_verified_amount,
            dashboardBilling.totalVerifiedAmount,
            oldStats.totalVerifiedAmount
        ));

        const outstandingAmount = safeNumber(firstValue(
            paymentBilling.outstanding_amount,
            paymentBilling.outstandingAmount,
            paymentSummary?.outstanding_amount,
            paymentSummary?.outstandingAmount,
            dashboardBilling.outstanding_amount,
            dashboardBilling.outstandingAmount,
            oldStats.outstandingAmount,
            Math.max(totalBill - totalVerified, 0)
        ));

        const teamRegistrationFee = safeNumber(firstValue(
            paymentBilling.team_registration_fee,
            paymentBilling.teamRegistrationFee,
            paymentSummary?.team_registration_fee,
            paymentSummary?.teamRegistrationFee,
            dashboardBilling.team_registration_fee,
            dashboardBilling.teamRegistrationFee,
            oldStats.teamRegistrationFee
        ));

        const totalAthleteAmount = safeNumber(firstValue(
            paymentBilling.total_athlete_amount,
            paymentBilling.totalAthleteAmount,
            paymentSummary?.total_athlete_amount,
            paymentSummary?.totalAthleteAmount,
            dashboardBilling.total_athlete_amount,
            dashboardBilling.totalAthleteAmount,
            oldStats.totalAthleteAmount,
            Math.max(totalBill - teamRegistrationFee, 0)
        ));

        const paymentStatus =
            latestPayment?.status ||
            oldStats.paymentStatus ||
            'Unpaid';

        const pendingEntries = safeNumber(firstValue(
            paymentBilling.pending_athletes,
            paymentBilling.pendingAthletes,
            paymentSummary?.pending_athletes,
            paymentSummary?.pendingAthletes,
            dashboardBilling.pending_athletes,
            dashboardBilling.pendingAthletes,
            oldStats.pendingAthletes,
            athletes.filter(athlete => athlete.payment_status !== 'Verified').length
        ));

        const verifiedEntries = safeNumber(firstValue(
            paymentBilling.verified_athletes,
            paymentBilling.verifiedAthletes,
            paymentSummary?.verified_athletes,
            paymentSummary?.verifiedAthletes,
            dashboardBilling.verified_athletes,
            dashboardBilling.verifiedAthletes,
            oldStats.verifiedAthletes,
            athletes.filter(athlete => athlete.payment_status === 'Verified').length
        ));

        const billingBreakdown = normalizeBillingBreakdown(
            paymentBilling.billing_breakdown ||
            paymentBilling.billingBreakdown ||
            paymentSummary?.billing_breakdown ||
            paymentSummary?.billingBreakdown ||
            dashboardBilling.billing_breakdown ||
            dashboardBilling.billingBreakdown ||
            oldStats.billingBreakdown ||
            []
        );

        return {
            totalEntries,
            kyorugiEntries,
            poomsaeEntries,
            prestasiEntries,
            festivalEntries,
            kyorugiPrestasi,
            kyorugiFestival,
            poomsaeIndividual,
            poomsaeFreestyle,
            poomsaePair,
            poomsaeTeam,
            totalBill,
            totalVerified,
            outstandingAmount,
            paymentStatus,
            pendingEntries,
            verifiedEntries,
            teamRegistrationFee,
            totalAthleteAmount,
            billingBreakdown,
            latestPayment
        };
    }, [dashboardData, athletes, paymentSummary]);

    const latestEntries = useMemo(() => {
        return [...athletes]
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
            .slice(0, 5);
    }, [athletes]);

    const paymentBadge = getStatusBadge(
        computedStats.paymentStatus,
        computedStats.outstandingAmount
    );

    const renderStatCard = ({ title, value, subtitle, icon, iconClass }) => (
        <div className="col-12 col-md-6 col-xl-3">
            <div className="card border-0 shadow-sm rounded-4 h-100 bg-white hover-up">
                <div className="card-body p-3 d-flex align-items-center gap-3">
                    <div
                        className={`${iconClass} rounded-3 d-flex align-items-center justify-content-center flex-shrink-0`}
                        style={{
                            width: '48px',
                            height: '48px'
                        }}
                    >
                        {icon}
                    </div>

                    <div className="overflow-hidden">
                        <p className="text-muted small fw-bold mb-0 text-uppercase" style={{ letterSpacing: '0.5px' }}>
                            {title}
                        </p>
                        <h3 className="fw-black text-dark m-0">{value}</h3>
                        {subtitle && (
                            <div className="small text-muted text-truncate">
                                {subtitle}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                <div className="spinner-border text-primary" role="status"></div>
            </div>
        );
    }

    if (dashboardData?.hasTeam === false) {
        return (
            <div className="container-fluid p-0 animate-fade-in">
                <div className="card border-0 shadow-sm rounded-4 bg-white">
                    <div className="card-body p-5 text-center">
                        <FaUsers size={58} className="text-primary mb-3" />
                        <h4 className="fw-bold text-dark mb-2">Kontingen Belum Tersedia</h4>
                        <p className="text-muted small mb-0">
                            Akun Anda belum terhubung dengan data kontingen. Hubungi admin untuk mengaktifkan akses manager.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid p-0 animate-fade-in">
            {/* HEADER */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                <div>
                    <h4 className="fw-bold mb-1 text-dark">Dashboard Kontingen</h4>
                    <p className="text-muted small mb-0">
                        Selamat datang kembali, <span className="fw-bold text-primary">{user?.name || 'Manager'}</span>
                    </p>
                </div>

                <div className="d-flex gap-2 flex-wrap">
                    <button
                        type="button"
                        className="btn btn-light border rounded-pill px-4 py-2 shadow-sm fw-bold d-flex align-items-center gap-2 hover-scale"
                        onClick={() => fetchDashboard(true)}
                        disabled={refreshing}
                        style={{ fontSize: '0.9rem' }}
                    >
                        {refreshing ? (
                            <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                            <FaSyncAlt />
                        )}
                        Refresh
                    </button>

                    <Link
                        to="/manager/atlet/tambah"
                        className="btn btn-primary rounded-pill px-4 py-2 shadow-sm fw-bold d-flex align-items-center gap-2 hover-scale"
                        style={{ fontSize: '0.9rem' }}
                    >
                        <FaPlus /> Daftarkan Atlet
                    </Link>
                </div>
            </div>

            {/* STATISTIK ROW */}
            <div className="row g-3 mb-4">
                {renderStatCard({
                    title: 'Total Entry',
                    value: computedStats.totalEntries,
                    subtitle: `${computedStats.prestasiEntries} Prestasi • ${computedStats.festivalEntries} Festival`,
                    icon: <FaUsers size={20} />,
                    iconClass: 'bg-primary bg-opacity-10 text-primary'
                })}

                {renderStatCard({
                    title: 'Kyorugi',
                    value: computedStats.kyorugiEntries,
                    subtitle: `${computedStats.kyorugiPrestasi} Prestasi • ${computedStats.kyorugiFestival} Festival`,
                    icon: <FaUserNinja size={20} />,
                    iconClass: 'bg-danger bg-opacity-10 text-danger'
                })}

                {renderStatCard({
                    title: 'Poomsae',
                    value: computedStats.poomsaeEntries,
                    subtitle: `${computedStats.poomsaeIndividual} Ind • ${computedStats.poomsaeFreestyle} Free • ${computedStats.poomsaePair} Pair • ${computedStats.poomsaeTeam} Team`,
                    icon: <FaTrophy size={20} />,
                    iconClass: 'bg-info bg-opacity-10 text-info'
                })}

                {renderStatCard({
                    title: 'Belum Lunas',
                    value: computedStats.pendingEntries,
                    subtitle: `${computedStats.verifiedEntries} entry terverifikasi`,
                    icon: <FaWallet size={20} />,
                    iconClass: computedStats.pendingEntries > 0
                        ? 'bg-warning bg-opacity-10 text-warning'
                        : 'bg-success bg-opacity-10 text-success'
                })}
            </div>

            <div className="row g-3">
                {/* MODUL PEMBAYARAN */}
                <div className="col-lg-7">
                    <div className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden">
                        <div className="card-header bg-white border-bottom-0 pt-4 pb-0 px-4 d-flex justify-content-between align-items-center">
                            <h6 className="fw-bold text-dark m-0 d-flex align-items-center gap-2">
                                <FaWallet className="text-primary" /> Ringkasan Tagihan
                            </h6>

                            <Link to="/manager/pembayaran" className="btn btn-sm btn-light text-primary fw-bold rounded-pill px-3">
                                Detail <FaArrowRight className="ms-1" size={10} />
                            </Link>
                        </div>

                        <div className="card-body p-4">
                            {computedStats.totalBill > 0 ? (
                                <div className="p-3 rounded-4 bg-light border">
                                    <div className="row g-3 align-items-center">
                                        <div className="col-md-7">
                                            <p className="small text-muted mb-1 fw-bold">Total Biaya Pendaftaran</p>
                                            <h4 className="fw-bold text-dark m-0">
                                                {formatRupiah(computedStats.totalBill)}
                                            </h4>

                                            <div className="mt-3 d-flex flex-column gap-1">
                                                {computedStats.teamRegistrationFee > 0 && (
                                                    <div className="small text-muted d-flex justify-content-between gap-2">
                                                        <span>Biaya Kontingen</span>
                                                        <span className="fw-bold text-dark">
                                                            {formatRupiah(computedStats.teamRegistrationFee)}
                                                        </span>
                                                    </div>
                                                )}

                                                <div className="small text-muted d-flex justify-content-between gap-2">
                                                    <span>Biaya Entry Atlet</span>
                                                    <span className="fw-bold text-dark">
                                                        {formatRupiah(computedStats.totalAthleteAmount)}
                                                    </span>
                                                </div>

                                                <div className="small text-muted d-flex justify-content-between gap-2">
                                                    <span>Terverifikasi</span>
                                                    <span className="fw-bold text-success">
                                                        {formatRupiah(computedStats.totalVerified)}
                                                    </span>
                                                </div>

                                                <div className="small text-muted d-flex justify-content-between gap-2">
                                                    <span>Sisa Tagihan</span>
                                                    <span className="fw-bold text-primary">
                                                        {formatRupiah(computedStats.outstandingAmount)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-5 text-md-end">
                                            <span className={`badge px-3 py-2 rounded-pill d-inline-flex align-items-center gap-2 ${paymentBadge.className}`}>
                                                {paymentBadge.icon} {paymentBadge.label}
                                            </span>

                                            {computedStats.latestPayment?.admin_note && computedStats.paymentStatus === 'Rejected' && (
                                                <div className="small text-danger fw-bold mt-2">
                                                    {computedStats.latestPayment.admin_note}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {computedStats.billingBreakdown.length > 0 && (
                                        <div className="mt-3 pt-3 border-top">
                                            <div className="small text-muted fw-bold mb-2 d-flex align-items-center gap-2">
                                                <FaReceipt /> Rincian Singkat
                                            </div>

                                            <div className="d-flex flex-column gap-2">
                                                {computedStats.billingBreakdown.slice(0, 4).map((item) => (
                                                    <div
                                                        key={item.key}
                                                        className="small bg-white rounded-3 px-3 py-2 d-flex justify-content-between gap-2"
                                                    >
                                                        <span className="text-muted">
                                                            {item.label}
                                                            {' '}
                                                            <span className="fw-bold">
                                                                × {item.key === 'team_registration' ? 1 : item.count}
                                                            </span>
                                                        </span>

                                                        <span className="fw-bold text-dark">
                                                            {formatRupiah(item.total)}
                                                        </span>
                                                    </div>
                                                ))}

                                                {computedStats.billingBreakdown.length > 4 && (
                                                    <div className="small text-muted fw-bold px-1">
                                                        +{computedStats.billingBreakdown.length - 4} rincian lainnya
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center p-4 bg-light rounded-4 border border-dashed">
                                    <p className="text-muted small fw-bold mb-2">Belum ada tagihan aktif.</p>
                                    <Link to="/manager/atlet/tambah" className="btn btn-sm btn-outline-primary rounded-pill px-3 fw-bold">
                                        Mulai Daftarkan Atlet
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* BREAKDOWN POOMSAE */}
                <div className="col-lg-5">
                    <div className="card border-0 shadow-sm rounded-4 h-100 bg-white overflow-hidden">
                        <div className="card-header bg-white border-bottom-0 pt-4 px-4">
                            <h6 className="fw-bold text-dark m-0 d-flex align-items-center gap-2">
                                <FaLayerGroup className="text-info" /> Ringkasan Kategori
                            </h6>
                        </div>

                        <div className="card-body p-4">
                            <div className="row g-2">
                                <div className="col-6">
                                    <div className="bg-light rounded-3 p-3">
                                        <div className="small text-muted fw-bold">Individual</div>
                                        <h5 className="fw-black text-dark m-0">{computedStats.poomsaeIndividual}</h5>
                                    </div>
                                </div>

                                <div className="col-6">
                                    <div className="bg-light rounded-3 p-3">
                                        <div className="small text-muted fw-bold">Freestyle</div>
                                        <h5 className="fw-black text-dark m-0">{computedStats.poomsaeFreestyle}</h5>
                                    </div>
                                </div>

                                <div className="col-6">
                                    <div className="bg-light rounded-3 p-3">
                                        <div className="small text-muted fw-bold">Pair</div>
                                        <h5 className="fw-black text-dark m-0">{computedStats.poomsaePair}</h5>
                                    </div>
                                </div>

                                <div className="col-6">
                                    <div className="bg-light rounded-3 p-3">
                                        <div className="small text-muted fw-bold">Team</div>
                                        <h5 className="fw-black text-dark m-0">{computedStats.poomsaeTeam}</h5>
                                    </div>
                                </div>
                            </div>

                            <div className="small text-muted mt-3 d-flex align-items-start gap-2">
                                <FaInfoCircle className="mt-1 flex-shrink-0" />
                                <span>
                                    Pair dan Team dihitung sebagai <b>1 entry</b>. Biaya kontingen dihitung <b>1 kali per tim</b>.
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ENTRY TERBARU */}
                <div className="col-lg-7">
                    <div className="card border-0 shadow-sm rounded-4 h-100 bg-white">
                        <div className="card-header bg-white border-bottom-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                            <h6 className="fw-bold text-dark m-0 d-flex align-items-center gap-2">
                                <FaUsers className="text-primary" /> Entry Terbaru
                            </h6>

                            <Link to="/manager/atlet" className="btn btn-sm btn-light text-primary fw-bold rounded-pill px-3">
                                Lihat Semua
                            </Link>
                        </div>

                        <div className="card-body p-4">
                            {latestEntries.length === 0 ? (
                                <div className="text-center p-4 bg-light rounded-4 border border-dashed">
                                    <p className="text-muted small fw-bold mb-2">Belum ada entry yang terdaftar.</p>
                                    <Link to="/manager/atlet/tambah" className="btn btn-sm btn-outline-primary rounded-pill px-3 fw-bold">
                                        Tambah Entry
                                    </Link>
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-2">
                                    {latestEntries.map(entry => {
                                        const members = getEntryMembers(entry);

                                        return (
                                            <div
                                                key={entry.uuid || entry.id}
                                                className="d-flex align-items-center justify-content-between gap-3 bg-light rounded-4 p-3"
                                            >
                                                <div className="overflow-hidden">
                                                    <div className="fw-bold text-dark text-truncate">
                                                        {getDisplayName(entry)}
                                                    </div>

                                                    <div className="small text-muted text-truncate">
                                                        {entry.competition_type || '-'} • {entry.type || '-'} • {entry.division || '-'} • {getClassLabel(entry)}
                                                    </div>

                                                    {members.length > 1 && (
                                                        <span className="badge bg-info bg-opacity-10 text-info border border-info rounded-pill mt-2">
                                                            {members.length} Nama / 1 Entry
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="text-end flex-shrink-0">
                                                    <span
                                                        className={`badge rounded-pill px-3 py-2 ${
                                                            entry.payment_status === 'Verified'
                                                                ? 'bg-success-subtle text-success border border-success'
                                                                : 'bg-warning-subtle text-warning border border-warning'
                                                        }`}
                                                    >
                                                        {entry.payment_status === 'Verified' ? 'Lunas' : 'Belum Lunas'}
                                                    </span>

                                                    <div className="small text-muted mt-1">
                                                        {formatDate(entry.createdAt)}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* INFO CARD */}
                <div className="col-lg-5">
                    <div className="card border-0 shadow-sm rounded-4 h-100 bg-primary text-white overflow-hidden position-relative">
                        <div className="position-absolute top-0 end-0 p-3 opacity-10">
                            <FaUsers size={120} />
                        </div>

                        <div className="card-body p-4 position-relative z-1 d-flex flex-column justify-content-center">
                            <h5 className="fw-bold mb-2">Kelola Tim Anda</h5>
                            <p className="small text-white-50 mb-4" style={{ lineHeight: '1.6' }}>
                                Pastikan seluruh nama atlet, kategori, kelas tanding, data Pair/Team, dan tagihan kontingen sudah benar sebelum melakukan pembayaran.
                            </p>

                            <div className="d-flex gap-2 flex-wrap">
                                <Link
                                    to="/manager/atlet"
                                    className="btn btn-light text-primary rounded-pill fw-bold shadow-sm px-4 py-2 hover-scale"
                                    style={{ fontSize: '0.9rem' }}
                                >
                                    Lihat Data Atlet
                                </Link>

                                <Link
                                    to="/manager/pembayaran"
                                    className="btn btn-outline-light rounded-pill fw-bold px-4 py-2 hover-scale"
                                    style={{ fontSize: '0.9rem' }}
                                >
                                    Pembayaran
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CSS */}
            <style>{`
                .animate-fade-in {
                    animation: fadeIn 0.4s ease-out forwards;
                }

                .fw-black {
                    font-weight: 900;
                }

                .hover-scale {
                    transition: transform 0.2s;
                }

                .hover-scale:hover {
                    transform: scale(1.03);
                }

                .hover-up {
                    transition: transform 0.2s, box-shadow 0.2s;
                }

                .hover-up:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 .5rem 1rem rgba(0,0,0,.08)!important;
                }

                .border-dashed {
                    border-style: dashed !important;
                    border-width: 2px !important;
                    border-color: #dee2e6 !important;
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};

export default ManagerDashboard;